// =============================================================================
// vocCertPdf.ts — T-314 Phase B
// =============================================================================
// Client-side cert PDF generator for VOC assessments. Produces a single A4
// "Letter of Competency" page matching the .docx letter templates in
// compliance/Certificates/_templates/CG-COC-*. Coretel-branded header,
// 3 body paragraphs personalised with the assessee's name + role, a fact
// table (VOC ref + dates + prerequisites), James's sign-off, verification
// footer.
//
// Pattern mirrors web/src/features/inspections/inspectionPdf.ts:
//   - Lazy-load pdfmake + vfs via ?url script tags
//   - Build a docDefinition object
//   - createPdf().download(filename)
//
// Phase C will add a Worker-side variant that persists to R2 and stamps
// signed_pdf_file_id on the assessment row. For now: instant browser
// download, no R2 roundtrip.
// =============================================================================

// @ts-ignore — Vite ?url suffix isn't in pdfmake's type defs
import pdfmakeScriptUrl  from "pdfmake/build/pdfmake.min.js?url";
// @ts-ignore
import vfsFontsScriptUrl from "pdfmake/build/vfs_fonts.js?url";

const COLOR = {
  primary:     "#ff7844",
  primaryWash: "#fff4ee",
  ink:         "#111111",
  text:        "#1a1a1a",
  muted:       "#666666",
  dim:         "#999999",
  rule:        "#e5e5e5",
};

// -----------------------------------------------------------------------------
// pdfmake loader (lazy, single-fetch)
// -----------------------------------------------------------------------------

function loadScriptOnce(src: string, tag: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[data-tag="${tag}"]`);
    if (existing) {
      if (existing.dataset.loaded === "true") { resolve(); return; }
      existing.addEventListener("load",  () => resolve());
      existing.addEventListener("error", () => reject(new Error(`Failed to load ${tag}`)));
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.dataset.tag = tag;
    script.onload  = () => { script.dataset.loaded = "true"; resolve(); };
    script.onerror = () => reject(new Error(`Failed to load ${tag} from ${src}`));
    document.head.appendChild(script);
  });
}

async function getPdfMake(): Promise<any> {
  const existing = (window as any).pdfMake;
  if (existing?.vfs && Object.keys(existing.vfs).length > 0) return existing;
  await loadScriptOnce(pdfmakeScriptUrl,  "pdfmake-core");
  await loadScriptOnce(vfsFontsScriptUrl, "pdfmake-vfs");
  return (window as any).pdfMake;
}

async function fetchLogoDataUrl(path: string): Promise<string | null> {
  try {
    const res = await fetch(path);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string>((resolve, reject) => {
      const fr = new FileReader();
      fr.onloadend = () => resolve(fr.result as string);
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    });
  } catch { return null; }
}

// -----------------------------------------------------------------------------
// Public types + entry
// -----------------------------------------------------------------------------

export interface VocCertPdfArgs {
  // Assessment + template + employee (passed by the caller, already loaded)
  voc_assessment_id:   string;
  template_code:       string;        // e.g. CG-VOC-SKL-002
  template_title:      string;        // e.g. Service Locator (Internal)
  asset_kind:          string;        // 'plant' | 'skill'
  validity_years:      number;
  assessed_at:         string;        // ISO timestamp
  outcome:             string;        // expected 'competent' for cert generation
  employee_full_name:  string;
  employee_role:       string;
  // Template intro (drives the body wording slightly per VOC type)
  intro_purpose?:      string | null;
  // Prerequisites — fed into the fact table summary line
  prerequisite_summary?: string | null;
  // Director signing
  director_name:       string;        // James O'Sullivan
  director_email:      string;        // hse@coretelgroup.com
  director_phone?:     string;
  // T-314 hotfix: embed the SVG signature into the cert. Defaults to the
  // assessor's signature on the assessment (since James-as-Director is also
  // the assessor for current Coretel VOC volume). If null, falls back to a
  // typed line. Long-term, a per-Director signature on file would replace
  // pulling from the assessment.
  director_signature_svg?: string | null;
  // Set when this certificate is a corrected reissue of one already sent. Printed
  // beneath the fact table. A compliance document that changes after issue must
  // say so on its face - a silently-different second copy is worse than the
  // original error, and it is the first thing an auditor will ask about.
  reissue_note?:       string | null;
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function fmtDate(iso: string): string {
  const d = new Date(iso);
  const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

function addYears(iso: string, years: number): string {
  const d = new Date(iso);
  d.setFullYear(d.getFullYear() + years);
  return d.toISOString();
}

function certCodeFromVocCode(vocCode: string): string {
  // CG-VOC-PLT-001 → CG-COC-PLT-001 (cert code mirrors the VOC code, swapping VOC → COC)
  return vocCode.replace("-VOC-", "-COC-");
}

function filenameFor(args: VocCertPdfArgs): string {
  const safeName = args.employee_full_name.replace(/[^a-z0-9_]+/gi, "_");
  const certCode = certCodeFromVocCode(args.template_code);
  const ymd = new Date(args.assessed_at).toISOString().slice(0, 10);
  return `${certCode}_${safeName}_${ymd}.pdf`;
}

// Body paragraph builder — generic 3-paragraph structure modelled on the
// .docx letter templates. Reads more like a real "To Whom It May Concern"
// letter than a form printout.
function bodyParagraphs(args: VocCertPdfArgs) {
  const name        = args.employee_full_name;
  const role        = args.employee_role || "Field Crew";
  const vocTitle    = args.template_title;
  const vocCode     = args.template_code;
  const purposeText = args.intro_purpose
    || "This Verification of Competency confirms that the assessee has the practical knowledge, skills and attitude to safely and competently perform the activities covered by this competency.";
  const prereqLine  = args.prerequisite_summary
    || "current prerequisite tickets sighted and recorded";

  return [
    { text: [
        { text: name, bold: true }, " is employed by Coretel Group Pty Ltd in a ", { text: role, italics: true },
        " role. ", purposeText,
        " The work is conducted on Coretel's own sites and on behalf of Coretel's clients across the telecommunications and civil construction sectors.",
      ], margin: [0, 8, 0, 8] as [number, number, number, number] },
    { text: [
        name, " has demonstrated competency through Coretel's internal Verification of Competency process (",
        { text: vocCode, bold: true }, " — ", vocTitle, "). ",
        prereqLine.charAt(0).toUpperCase() + prereqLine.slice(1),
        ". ", name, " has practical experience consistent with Coretel operations.",
      ], margin: [0, 0, 0, 8] as [number, number, number, number] },
    { text: [
        "Coretel Group Pty Ltd confirms that ", name,
        " is competent to perform the activities covered by ",
        { text: vocCode, bold: true },
        " on Coretel and client worksites. This competency is current and the assessment record is held on file.",
      ], margin: [0, 0, 0, 8] as [number, number, number, number] },
  ];
}

// -----------------------------------------------------------------------------
// Main export
// -----------------------------------------------------------------------------

export async function downloadVocCertPdf(args: VocCertPdfArgs): Promise<void> {
  if (args.outcome !== "competent") {
    throw new Error("Cert PDF only generates for competent assessments.");
  }

  const pdfMake = await getPdfMake();
  const logoData = await fetchLogoDataUrl("/coretel-logo-full.png");
  const certCode = certCodeFromVocCode(args.template_code);
  const expiryIso = addYears(args.assessed_at, args.validity_years);

  const docDefinition: any = {
    info: {
      title:   `${certCode} Letter of Competency — ${args.employee_full_name}`,
      author:  "Coretel Group Pty Ltd",
      subject: `Letter of Competency — ${args.template_title}`,
    },
    pageSize: "A4",
    pageMargins: [50, 50, 50, 70],

    content: [
      // Header band
      {
        columns: [
          logoData
            ? { image: logoData, width: 90, alignment: "left" }
            : { text: "CORETEL", color: COLOR.primary, bold: true, fontSize: 22 },
          {
            stack: [
              { text: "CORETEL GROUP PTY LTD", bold: true,  fontSize: 11, alignment: "right", color: COLOR.ink },
              { text: "ABN 63 690 407 679",                  fontSize:  9, alignment: "right", color: COLOR.muted, margin: [0, 2, 0, 0] },
              { text: "Web: www.coretelgroup.com",           fontSize:  9, alignment: "right", color: COLOR.muted },
              { text: `${certCode} • v1.0`,                  fontSize:  8, alignment: "right", color: COLOR.dim, italics: true, margin: [0, 6, 0, 0] },
            ],
          },
        ],
      },
      // Coloured rule under header
      { canvas: [{ type: "line", x1: 0, y1: 0, x2: 495, y2: 0, lineWidth: 2, lineColor: COLOR.primary }], margin: [0, 12, 0, 16] },

      // Date
      { text: fmtDate(args.assessed_at), color: COLOR.muted, margin: [0, 0, 0, 16] },

      // Salutation
      { text: "To Whom It May Concern,", bold: true, margin: [0, 0, 0, 12] },

      // Body — 3 paragraphs
      ...bodyParagraphs(args),

      // Fact table
      {
        margin: [0, 12, 0, 16],
        table: {
          widths: [140, "*"],
          body: [
            [
              { text: "VOC reference",      bold: true, fillColor: COLOR.primaryWash, margin: [6, 6, 6, 6] },
              { text: `${args.template_code} — ${args.template_title}`, margin: [6, 6, 6, 6] },
            ],
            [
              { text: "Date of assessment", bold: true, fillColor: COLOR.primaryWash, margin: [6, 6, 6, 6] },
              { text: fmtDate(args.assessed_at), margin: [6, 6, 6, 6] },
            ],
            [
              { text: "Re-assessment due",  bold: true, fillColor: COLOR.primaryWash, margin: [6, 6, 6, 6] },
              { text: fmtDate(expiryIso),   margin: [6, 6, 6, 6] },
            ],
            [
              { text: "Prerequisite tickets", bold: true, fillColor: COLOR.primaryWash, margin: [6, 6, 6, 6] },
              { text: args.prerequisite_summary || "Sighted on file at time of assessment", margin: [6, 6, 6, 6] },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => COLOR.rule,
          vLineColor: () => COLOR.rule,
        },
      },

      // Corrected reissue note - only when the caller says so.
      //
      // Deliberately a single quiet line rather than a boxed callout. The first
      // version was a filled table and pushed the signature block onto a second
      // page, which looks like something went wrong with the document - the
      // opposite of the reassurance it is there to give. It still says what
      // changed; it just stops shouting about it.
      ...(args.reissue_note
        ? [{
            text: args.reissue_note,
            fontSize: 8,
            italics: true,
            color: COLOR.muted,
            margin: [0, 10, 0, 0] as [number, number, number, number],
          }]
        : []),

      // Sign-off block
      { text: "Regards,", margin: [0, 16, 0, 8] },
      // T-314 hotfix: embed signature SVG above the line. pdfmake's `svg`
      // type natively renders the path strokes captured by SignaturePad,
      // so the cert carries the actual ink not just a typed name.
      args.director_signature_svg
        ? { svg: args.director_signature_svg, width: 220, height: 70, margin: [0, 0, 0, 0] as [number, number, number, number] }
        : { text: " ", margin: [0, 0, 0, 32] as [number, number, number, number] },
      { canvas: [{ type: "line", x1: 0, y1: 0, x2: 240, y2: 0, lineWidth: 0.5, lineColor: COLOR.ink }], margin: [0, 0, 0, 4] },
      { text: args.director_name, bold: true, margin: [0, 0, 0, 2] },
      { text: "Director — Coretel Group Pty Ltd", color: COLOR.muted, margin: [0, 0, 0, 6] },
      {
        text: [
          ...(args.director_phone ? [{ text: `M: ${args.director_phone}   `, color: COLOR.muted as any }] : []),
          { text: `E: ${args.director_email}`, color: COLOR.muted as any },
        ],
        fontSize: 9,
      },
    ],

    defaultStyle: { fontSize: 10, color: COLOR.text, lineHeight: 1.35 },

    footer: (currentPage: number, pageCount: number) => ({
      margin: [50, 0, 50, 24],
      columns: [
        {
          text: [
            { text: "To verify this certificate, email ",                                                color: COLOR.muted },
            { text: args.director_email,                                                                  bold: true, color: COLOR.ink },
            { text: ` quoting ${certCode} and the employee name.`,                                       color: COLOR.muted },
          ],
          fontSize: 8,
          alignment: "left",
        },
        {
          text: `Page ${currentPage} of ${pageCount}`,
          fontSize: 8,
          alignment: "right",
          color: COLOR.dim,
        },
      ],
    }),
  };

  const pdfDocGen = pdfMake.createPdf(docDefinition);
  pdfDocGen.download(filenameFor(args));
}
